/**
 * Admin Workshops Tab
 * Workshop management content for the admin console
 * Includes exercise management within workshops
 */

import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useWorkshops,
  useCreateWorkshop,
  useUpdateWorkshop,
  useDeleteWorkshop,
} from "@hooks/useWorkshops";
import {
  useWorkshopExercises,
  useCreateExercise,
  useUpdateExercise,
  useDeleteExercise,
} from "@hooks/useExercises";
import {
  DataTable,
  type Column,
  ConfirmModal,
  BulkActionsBar,
  SearchInput,
  StatusBadge,
  WorkshopForm,
  ExerciseForm,
  Modal,
  ExercisePreview,
} from "@components/admin";
import { Button } from "@components/common";
import { formatDate } from "@utils/format";
import type {
  Workshop,
  WorkshopStatus,
  WorkshopDifficulty,
  Exercise,
} from "@/types";
import type {
  WorkshopCreateRequest,
  WorkshopUpdateRequest,
  ExerciseCreateRequest,
  ExerciseUpdateRequest,
} from "@/types/api";
import { showToast } from "@stores/uiStore";

type SortDirection = "asc" | "desc";

// Status badge variant mapping
const statusVariants: Record<
  WorkshopStatus,
  "success" | "warning" | "neutral"
> = {
  active: "success",
  draft: "warning",
  completed: "success",
  archived: "neutral",
};

// Difficulty badge variant mapping
const difficultyVariants: Record<
  WorkshopDifficulty,
  "success" | "warning" | "info"
> = {
  beginner: "success",
  intermediate: "warning",
  advanced: "info",
};

// Format duration in human-readable form
function formatDuration(minutes: number | null | undefined): string {
  if (!minutes) return "-";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

export function AdminWorkshopsTab() {
  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<WorkshopStatus | "all">(
    "all",
  );
  const [sortColumn, setSortColumn] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Workshop Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState<Workshop | null>(null);
  const [deletingWorkshops, setDeletingWorkshops] = useState<string[]>([]);

  // Exercise Management
  const [managingExercisesFor, setManagingExercisesFor] =
    useState<Workshop | null>(null);
  const [isExerciseCreateOpen, setIsExerciseCreateOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);
  const [deletingExercise, setDeletingExercise] = useState<Exercise | null>(
    null,
  );
  const [previewingExercise, setPreviewingExercise] = useState<Exercise | null>(
    null,
  );

  const navigate = useNavigate();

  // Queries & mutations
  const { data: workshopsData, isLoading, refetch } = useWorkshops();

  const createWorkshop = useCreateWorkshop();
  const updateWorkshop = useUpdateWorkshop();
  const deleteWorkshop = useDeleteWorkshop();

  // Exercise queries - only fetch when managing exercises
  const { data: exercises = [], refetch: refetchExercises } =
    useWorkshopExercises(managingExercisesFor?.id);
  const createExercise = useCreateExercise();
  const updateExercise = useUpdateExercise();
  const deleteExercise = useDeleteExercise();

  // Filter and sort workshops
  const filteredWorkshops = useMemo(() => {
    const workshops = workshopsData ?? [];
    let result = [...workshops];

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (workshop) =>
          workshop.title.toLowerCase().includes(searchLower) ||
          (workshop.description?.toLowerCase().includes(searchLower) ?? false),
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      result = result.filter((workshop) => workshop.status === statusFilter);
    }

    // Sort
    result.sort((a, b) => {
      const aVal = (a as unknown as Record<string, unknown>)[sortColumn];
      const bVal = (b as unknown as Record<string, unknown>)[sortColumn];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      let comparison: number;
      if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal;
      } else if (sortColumn.includes("date") || sortColumn.includes("_at")) {
        comparison =
          new Date(aVal as string).getTime() -
          new Date(bVal as string).getTime();
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return result;
  }, [workshopsData, search, statusFilter, sortColumn, sortDirection]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [sortColumn],
  );

  // Table columns
  const columns: Column<Workshop>[] = useMemo(
    () => [
      {
        key: "title",
        header: "Title",
        sortable: true,
        render: (workshop) => (
          <div>
            <div className="font-medium text-gray-900 dark:text-white">
              {workshop.title}
            </div>
            {workshop.description && (
              <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                {workshop.description.slice(0, 60)}
                {workshop.description.length > 60 ? "..." : ""}
              </div>
            )}
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        width: "100px",
        render: (workshop) => (
          <StatusBadge
            label={
              workshop.status.charAt(0).toUpperCase() + workshop.status.slice(1)
            }
            variant={statusVariants[workshop.status]}
          />
        ),
      },
      {
        key: "difficulty_level",
        header: "Difficulty",
        sortable: true,
        width: "110px",
        render: (workshop) =>
          workshop.difficulty_level ? (
            <StatusBadge
              label={
                workshop.difficulty_level.charAt(0).toUpperCase() +
                workshop.difficulty_level.slice(1)
              }
              variant={difficultyVariants[workshop.difficulty_level]}
            />
          ) : (
            <span className="text-gray-400 text-sm">-</span>
          ),
      },
      {
        key: "estimated_minutes",
        header: "Duration",
        sortable: true,
        width: "90px",
        render: (workshop) => (
          <span className="text-gray-600 dark:text-gray-400 text-sm">
            {formatDuration(workshop.estimated_minutes)}
          </span>
        ),
      },
      {
        key: "start_date",
        header: "Start Date",
        sortable: true,
        width: "120px",
        render: (workshop) => (
          <span className="text-gray-500 dark:text-gray-400 text-sm">
            {workshop.start_date ? formatDate(workshop.start_date) : "-"}
          </span>
        ),
      },
      {
        key: "end_date",
        header: "End Date",
        sortable: true,
        width: "120px",
        render: (workshop) => (
          <span className="text-gray-500 dark:text-gray-400 text-sm">
            {workshop.end_date ? formatDate(workshop.end_date) : "-"}
          </span>
        ),
      },
      {
        key: "created_at",
        header: "Created",
        sortable: true,
        width: "120px",
        render: (workshop) => (
          <span className="text-gray-500 dark:text-gray-400 text-sm">
            {formatDate(workshop.created_at)}
          </span>
        ),
      },
      {
        key: "actions",
        header: "",
        width: "180px",
        render: (workshop) => (
          <div className="flex justify-end gap-1">
            {/* Preview Workshop */}
            <button
              onClick={() =>
                navigate(`/admin/workshops/${workshop.id}/preview`)
              }
              className="p-1.5 text-gray-400 hover:text-primary-500 transition-colors"
              title="Preview workshop"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </button>
            {/* Manage Exercises */}
            <button
              onClick={() => setManagingExercisesFor(workshop)}
              className="p-1.5 text-gray-400 hover:text-primary-500 transition-colors"
              title="Manage exercises"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            </button>
            {/* Edit */}
            <button
              onClick={() => setEditingWorkshop(workshop)}
              className="p-1.5 text-gray-400 hover:text-primary-500 transition-colors"
              title="Edit workshop"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            {/* Delete */}
            <button
              onClick={() => setDeletingWorkshops([workshop.id])}
              className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              title="Delete workshop"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        ),
      },
    ],
    [navigate],
  );

  // Bulk actions
  const bulkActions = useMemo(
    () => [
      {
        label: "Activate",
        onClick: async () => {
          const ids = Array.from(selectedIds);
          const promises = ids.map((id) =>
            updateWorkshop.mutateAsync({ id, data: { status: "active" } }),
          );
          try {
            await Promise.all(promises);
            showToast(`${ids.length} workshop(s) activated`, "success");
            setSelectedIds(new Set());
          } catch {
            showToast("Failed to activate workshops", "error");
          }
        },
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        ),
      },
      {
        label: "Archive",
        onClick: async () => {
          const ids = Array.from(selectedIds);
          const promises = ids.map((id) =>
            updateWorkshop.mutateAsync({ id, data: { status: "archived" } }),
          );
          try {
            await Promise.all(promises);
            showToast(`${ids.length} workshop(s) archived`, "success");
            setSelectedIds(new Set());
          } catch {
            showToast("Failed to archive workshops", "error");
          }
        },
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
            />
          </svg>
        ),
      },
      {
        label: "Delete",
        onClick: () => {
          setDeletingWorkshops(Array.from(selectedIds));
        },
        variant: "danger" as const,
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
      },
    ],
    [selectedIds, updateWorkshop],
  );

  // Workshop CRUD handlers
  const handleCreateWorkshop = async (
    data: WorkshopCreateRequest | WorkshopUpdateRequest,
  ) => {
    await createWorkshop.mutateAsync(data as WorkshopCreateRequest);
    showToast("Workshop created successfully", "success");
    setIsCreateModalOpen(false);
  };

  const handleUpdateWorkshop = async (
    data: WorkshopCreateRequest | WorkshopUpdateRequest,
  ) => {
    if (!editingWorkshop) return;
    await updateWorkshop.mutateAsync({
      id: editingWorkshop.id,
      data: data as WorkshopUpdateRequest,
    });
    showToast("Workshop updated successfully", "success");
    setEditingWorkshop(null);
  };

  const handleDeleteWorkshops = async () => {
    const promises = deletingWorkshops.map((id) =>
      deleteWorkshop.mutateAsync(id),
    );
    try {
      await Promise.all(promises);
      showToast(`${deletingWorkshops.length} workshop(s) deleted`, "success");
      setDeletingWorkshops([]);
      setSelectedIds(new Set());
    } catch {
      showToast("Failed to delete workshops", "error");
    }
  };

  // Exercise CRUD handlers
  const handleCreateExercise = async (
    data: ExerciseCreateRequest | ExerciseUpdateRequest,
  ) => {
    await createExercise.mutateAsync(data as ExerciseCreateRequest);
    showToast("Exercise added successfully", "success");
    setIsExerciseCreateOpen(false);
    refetchExercises();
  };

  const handleUpdateExercise = async (
    data: ExerciseCreateRequest | ExerciseUpdateRequest,
  ) => {
    if (!editingExercise || !managingExercisesFor) return;
    await updateExercise.mutateAsync({
      id: editingExercise.id,
      data: data as ExerciseUpdateRequest,
      workshopId: managingExercisesFor.id,
    });
    showToast("Exercise updated successfully", "success");
    setEditingExercise(null);
    refetchExercises();
  };

  const handleDeleteExercise = async () => {
    if (!deletingExercise || !managingExercisesFor) return;
    await deleteExercise.mutateAsync({
      id: deletingExercise.id,
      workshopId: managingExercisesFor.id,
    });
    showToast("Exercise deleted successfully", "success");
    setDeletingExercise(null);
    refetchExercises();
  };

  // Sort exercises by order_index
  const sortedExercises = useMemo(() => {
    return [...exercises].sort((a, b) => a.order_index - b.order_index);
  }, [exercises]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Workshops Management
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Create and manage training workshops
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Add Workshop
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search workshops..."
          className="w-64"
        />
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as WorkshopStatus | "all")
          }
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </select>
        <button
          onClick={() => refetch()}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Refresh"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      {/* Bulk Actions */}
      <BulkActionsBar
        selectedCount={selectedIds.size}
        actions={bulkActions}
        onClearSelection={() => setSelectedIds(new Set())}
      />

      {/* Data Table */}
      <DataTable
        data={filteredWorkshops}
        columns={columns}
        keyExtractor={(workshop) => workshop.id}
        isLoading={isLoading}
        emptyMessage="No workshops found"
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
      />

      {/* Create Workshop Modal */}
      <WorkshopForm
        key="create-form"
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateWorkshop}
        isLoading={createWorkshop.isPending}
        mode="create"
      />

      {/* Edit Workshop Modal */}
      {editingWorkshop && (
        <WorkshopForm
          key={`edit-${editingWorkshop.id}`}
          isOpen={!!editingWorkshop}
          onClose={() => setEditingWorkshop(null)}
          onSubmit={handleUpdateWorkshop}
          isLoading={updateWorkshop.isPending}
          workshop={editingWorkshop}
          mode="edit"
        />
      )}

      {/* Delete Workshop Confirmation */}
      <ConfirmModal
        isOpen={deletingWorkshops.length > 0}
        onClose={() => setDeletingWorkshops([])}
        onConfirm={handleDeleteWorkshops}
        title="Delete Workshops"
        message={`Are you sure you want to delete ${deletingWorkshops.length} workshop(s)? This will also delete all exercises within them. This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        isLoading={deleteWorkshop.isPending}
      />

      {/* Exercise Management Modal */}
      <Modal
        isOpen={!!managingExercisesFor}
        onClose={() => setManagingExercisesFor(null)}
        title={`Exercises: ${managingExercisesFor?.title ?? ""}`}
        description="Manage exercises for this workshop"
        size="xl"
        footer={
          <div className="flex justify-end">
            <Button
              variant="ghost"
              onClick={() => setManagingExercisesFor(null)}
            >
              Close
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Add Exercise Button */}
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setIsExerciseCreateOpen(true)}>
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Add Exercise
            </Button>
          </div>

          {/* Exercise List */}
          {sortedExercises.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <svg
                className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <p>No exercises yet. Add your first exercise to get started.</p>
            </div>
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
              {sortedExercises.map((exercise) => (
                <div
                  key={exercise.id}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-mono text-gray-400 w-8">
                      #{exercise.order_index}
                    </span>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {exercise.title}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                          {exercise.content_type}
                        </span>
                        {exercise.content_path && (
                          <span className="font-mono text-xs truncate max-w-48">
                            {exercise.content_path}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {/* Preview Exercise */}
                    <button
                      onClick={() => setPreviewingExercise(exercise)}
                      className="p-1.5 text-gray-400 hover:text-primary-500 transition-colors"
                      title="Preview exercise"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                    {/* Edit Exercise */}
                    <button
                      onClick={() => setEditingExercise(exercise)}
                      className="p-1.5 text-gray-400 hover:text-primary-500 transition-colors"
                      title="Edit exercise"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    {/* Delete Exercise */}
                    <button
                      onClick={() => setDeletingExercise(exercise)}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete exercise"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Create Exercise Modal */}
      {managingExercisesFor && (
        <ExerciseForm
          key="create-exercise"
          isOpen={isExerciseCreateOpen}
          onClose={() => setIsExerciseCreateOpen(false)}
          onSubmit={handleCreateExercise}
          isLoading={createExercise.isPending}
          workshopId={managingExercisesFor.id}
          mode="create"
          defaultOrderIndex={sortedExercises.length}
        />
      )}

      {/* Edit Exercise Modal */}
      {editingExercise && managingExercisesFor && (
        <ExerciseForm
          key={`edit-exercise-${editingExercise.id}`}
          isOpen={!!editingExercise}
          onClose={() => setEditingExercise(null)}
          onSubmit={handleUpdateExercise}
          isLoading={updateExercise.isPending}
          exercise={editingExercise}
          workshopId={managingExercisesFor.id}
          mode="edit"
        />
      )}

      {/* Delete Exercise Confirmation */}
      <ConfirmModal
        isOpen={!!deletingExercise}
        onClose={() => setDeletingExercise(null)}
        onConfirm={handleDeleteExercise}
        title="Delete Exercise"
        message={`Are you sure you want to delete "${deletingExercise?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        isLoading={deleteExercise.isPending}
      />

      {/* Exercise Preview Modal */}
      {previewingExercise && (
        <ExercisePreview
          exercise={previewingExercise}
          isOpen={!!previewingExercise}
          onClose={() => setPreviewingExercise(null)}
        />
      )}
    </div>
  );
}
